/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { api } from "@/api";
import type { ProductData } from "@/components/common/HeaderComponents/types";
import type { ProductGroup } from "@/pages/support/TechnicalSupportComponents/types";
import {
  toProductGroups,
  flattenProductNames,
  toSlugNameMap,
  toLicensedProductNames,
  toUnlicensedProductNames,
  toNonRefundableProductNames,
  reportUnknownProductNames,
  reportUnlistedProductSlugs,
  toHeaderHiddenProductNames,
} from "./productCatalogue";
import { PRODUCT_SLUGS } from "@/data/productCatalog";
import { PRODUCT_QUERY_PARAM_MAP_VALUES } from "./catalogueChecks";

/** The shape `support-catalogue/` returns. */
interface SupportCatalogueResponse {
  mobile_apps: ProductGroup;
  /**
   * N-17: the catch-all option. Optional so a cached response cut before this
   * field shipped still loads the Android products rather than failing the
   * whole request — the dropdown then behaves exactly as it did before.
   */
  other_product?: ProductGroup;
}

export interface ProductCatalogueOptions {
  /**
   * Fetch a fresh copy behind the cached one. Default true.
   *
   * The support pages need it — being current is the whole point of the change.
   * The header sets it to false: the shared product-data throttle allows 200
   * requests an hour per address, and one extra request per page load for every
   * visitor risks throttling errors behind a single office address, to correct a
   * menu entry for at most five minutes (decision 7.3).
   */
  refresh?: boolean;
  /**
   * Also load the Android applications, which have no row in the Product table
   * and so cannot come from the catalogue. Default false.
   *
   * The support pages set it. The header must not: an Android product in the
   * Products menu would link to a page that does not exist (IND-38).
   */
  includeMobileApps?: boolean;
}

export interface ProductCatalogue {
  /** The raw response, for consumers that need slugs rather than grouped names. */
  productData: ProductData | null;
  productGroups: ProductGroup[];
  productNames: string[];
  slugNameMap: Record<string, string>;
  /**
   * The Android applications, or null when they were not asked for. Loaded with
   * the catalogue and treated as part of it: if either fails, the list is not
   * complete and the page says so rather than quietly offering fewer products.
   */
  mobileAppGroup: ProductGroup | null;
  /**
   * N-17: the catch-all option, or null when it was not asked for or the server
   * did not send it. Carries the canonical value the server accepts, with a
   * translated heading and label attached for display only.
   *
   * Unlike the Android list, a missing value here is not treated as a failed
   * load: the catalogue is still complete without it, and the forms it belongs
   * on keep working exactly as they did before this option existed.
   */
  otherProductGroup: ProductGroup | null;
  /** IND-7: derived from what Django publishes, not from a list kept here. */
  licensedProductNames: string[];
  unlicensedProductNames: string[];
  nonRefundableProductNames: string[];
  /** True only while there is nothing to show. A background refresh is not loading. */
  productsLoading: boolean;
  /** True only when the list could not be obtained at all. */
  productsError: boolean;
  retry: () => void;
}

/**
 * Loads the active product catalogue for the support forms.
 *
 * Cached copy first, then a refresh behind it. That is what makes an admin
 * change appear on the next page load (V-7) while still painting the dropdown
 * immediately for a visitor who already has the data.
 *
 * The shared cache is never cleared (IND-9): the refresh below still writes the
 * cache, so the header navigation benefits from this request instead of losing
 * the copy it just paid for.
 */
export function useProductCatalogue(
  options: ProductCatalogueOptions = {},
): ProductCatalogue {
  const { refresh = true, includeMobileApps = false } = options;
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const [data, setData] = useState<ProductData | null>(null);
  const [mobileAppGroup, setMobileAppGroup] = useState<ProductGroup | null>(
    null,
  );
  // N-17: held as the server sent it. The translated labels are derived below
  // rather than stored, so switching language re-renders the right text without
  // another request.
  const [rawOtherProductGroup, setRawOtherProductGroup] =
    useState<ProductGroup | null>(null);
  const [productsLoading, setLoading] = useState(true);
  const [productsError, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    let haveData = false;
    setError(false);

    const apply = (next: ProductData) => {
      if (cancelled) return;
      haveData = true;
      setData(next);
      setLoading(false);
    };

    const load = async () => {
      // 0. The Android applications, when the caller needs them. Started here
      //    but awaited at step 2, so it travels alongside the catalogue instead
      //    of in front of it — awaiting it here would add a round trip to every
      //    support page load before anything could paint.
      const mobileRequest = includeMobileApps
        ? api.products.getSupportCatalogue<SupportCatalogueResponse>()
        : null;
      // Claim the rejection now. Without this the browser reports an unhandled
      // rejection in the window between the request failing and step 2 reading
      // it; the real handling still happens there.
      mobileRequest?.catch(() => {});

      // 1. Cached copy, if any. Paints at once; may be up to five minutes old.
      let cached: ProductData | null = null;
      try {
        cached = await api.products.getHeader<ProductData>();
      } catch {
        // Ignored on purpose — the refresh below is the authoritative attempt.
      }

      // 2. The Android list, requested above and read here. It is applied
      //    before the cached catalogue paints, so the dropdown is never shown
      //    as ready while eight products are still missing from it.
      if (mobileRequest) {
        try {
          const support = await mobileRequest;
          if (cancelled) return;
          if (!support?.mobile_apps?.items?.length) {
            throw new Error(
              "support-catalogue returned no mobile applications",
            );
          }
          setMobileAppGroup(support.mobile_apps);
          // N-17: read from the same response, and deliberately after the
          // Android check. Its absence is not a failure — see the note on
          // `otherProductGroup`.
          setRawOtherProductGroup(
            support.other_product?.items?.length ? support.other_product : null,
          );
        } catch (error) {
          if (cancelled) return;
          Sentry.captureException(
            error instanceof Error ? error : new Error(String(error)),
            {
              tags: {
                hook: "useProductCatalogue",
                request: "support-catalogue",
              },
            },
          );
          setLoading(false);
          setError(true);
          return;
        }
      }

      if (cached) apply(cached);

      // 3. Authoritative copy. Skips the cache read, still writes the cache.
      if (!refresh) {
        if (!cancelled && !haveData) {
          setLoading(false);
          setError(true);
        }
        return;
      }

      try {
        const fresh = await api.products.getHeader<ProductData>({
          forceRefresh: true,
        });
        if (!fresh) throw new Error("product-header returned no data");
        apply(fresh);
      } catch (error) {
        if (cancelled) return;
        Sentry.captureException(
          error instanceof Error ? error : new Error(String(error)),
          { tags: { hook: "useProductCatalogue" } },
        );
        setLoading(false);
        setError(!haveData); // only an error when there is nothing to show
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [language, attempt, refresh, includeMobileApps]);

  // IND-18: these must keep the same identity between renders. Consumers derive
  // further arrays from them, and a fresh array on every render rebinds document
  // listeners and rebuilds validation schemas.
  const productGroups = useMemo(
    () => (data ? toProductGroups(data, language) : []),
    [data, language],
  );

  const productNames = useMemo(
    () => flattenProductNames(productGroups),
    [productGroups],
  );

  const slugNameMap = useMemo(() => (data ? toSlugNameMap(data) : {}), [data]);

  // N-17: the canonical value stays exactly as the server sent it — that is
  // what the form submits and what the server validates. Only the heading and
  // the visible label are translated, so the option reads correctly in all six
  // languages without this server holding copy in any of them.
  //
  // IND-18: stable identity between renders, like the lists above.
  const otherProductGroup = useMemo<ProductGroup | null>(() => {
    if (!rawOtherProductGroup) return null;
    return {
      ...rawOtherProductGroup,
      groupLabel: t("support_pages.technical_support.other_product_group"),
      itemLabels: Object.fromEntries(
        rawOtherProductGroup.items.map((item) => [
          item,
          t("support_pages.technical_support.other_product"),
        ]),
      ),
    };
  }, [rawOtherProductGroup, t]);

  // IND-18: stable identities. Consumers build validation schemas from these.
  const licensedProductNames = useMemo(
    () => (data ? toLicensedProductNames(data) : []),
    [data],
  );

  const unlicensedProductNames = useMemo(
    () => (data ? toUnlicensedProductNames(data) : []),
    [data],
  );

  const nonRefundableProductNames = useMemo(
    () => (data ? toNonRefundableProductNames(data) : []),
    [data],
  );

  // IND-6 / IND-39: the two lists that still compare fixed text against a live
  // catalogue. Reported once per load, never thrown.
  //  - PRODUCT_QUERY_PARAM_MAP maps twelve published legacy links to product
  //    names. A rename in admin stops a link prefilling, silently (7.6.4).
  //  - The static slug list feeds the sitemap and conversion reporting, and
  //    cannot notice a product page it was never told about.
  useEffect(() => {
    if (!data) return;
    reportUnknownProductNames(
      flattenProductNames(productGroups),
      PRODUCT_QUERY_PARAM_MAP_VALUES,
      "PRODUCT_QUERY_PARAM_MAP",
    );
    reportUnlistedProductSlugs(
      data,
      PRODUCT_SLUGS,
      // Session 423 / F-13: read out of the catalogue rather than out of a
      // name list, so this check and the menu cannot disagree.
      toHeaderHiddenProductNames(data),
    );
  }, [data, productGroups]);

  return {
    productData: data,
    productGroups,
    productNames,
    slugNameMap,
    mobileAppGroup,
    otherProductGroup,
    licensedProductNames,
    unlicensedProductNames,
    nonRefundableProductNames,
    productsLoading,
    productsError,
    retry,
  };
}
